import { useMemo, useState } from 'react';
import Modal from '../Modal';
import commitmentsService from '../../services/commitments.service';
import {
  ADMIN_CANCEL_CATEGORIES_REQUIRING_REFERENCE,
  type AdminCancelCategory,
  type AdminCancelSource,
  type Commitment,
} from '../../types/commitments';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';

interface Props {
  commitment: Commitment;
  onClose: () => void;
  onCancelled: (message: string) => void;
}

const MAX_REASON = 1000;
const MAX_REF = 200;

const CATEGORY_OPTIONS: ReadonlyArray<{ value: AdminCancelCategory; label: string; description: string }> = [
  { value: 'fraud',                   label: 'Fraud',                       description: 'Suspected or confirmed fraudulent activity tied to this commitment.' },
  { value: 'regulatory_action',       label: 'Regulatory action',           description: 'Regulator ordered the cancellation (external folio required).' },
  { value: 'legal_dispute',           label: 'Legal dispute',               description: 'Court order / judicial process (oficio judicial required).' },
  { value: 'kyc_failed_revalidation', label: 'KYC failed revalidation',     description: 'Client failed periodic KYC; future debits must stop.' },
  { value: 'client_deceased',         label: 'Client deceased',             description: 'Client passed away; commitment stops, beneficiaries handled separately.' },
  { value: 'client_incapacitated',    label: 'Client incapacitated',        description: 'Client legally incapacitated.' },
  { value: 'duplicate',               label: 'Duplicate commitment',        description: 'Created twice by mistake or by retry.' },
  { value: 'data_error',              label: 'Data error',                  description: 'Wrong amount / product / cadence; the original agent is unavailable to use the normal flow.' },
  { value: 'other',                   label: 'Other (explain in reason)',   description: 'None of the above — narrative must justify the decision in detail.' },
];

const SOURCE_OPTIONS: ReadonlyArray<{ value: AdminCancelSource; label: string; description: string }> = [
  { value: 'manager_manual',    label: 'Manager (this user)',     description: 'You are cancelling manually from the UI.' },
  { value: 'compliance_ticket', label: 'Compliance ticket',       description: 'Acting on a ticket from the compliance team.' },
  { value: 'automated_job',     label: 'Automated job (rare UI)', description: 'Set only when escalating a job-flagged item into a manual cancel for the trail.' },
];

/**
 * Force-cancel modal — bypasses the normal request/approve cycle.
 *
 * Structured fields mirror the backend `AdminCancelBody`: the server
 * is the source of truth on what's required (category + reason
 * always; external_reference for regulatory_action and legal_dispute),
 * but we mirror the rule client-side to short-circuit the round-trip
 * to a 400. The submit button stays disabled until the form is valid.
 */
export default function AdminCancelCommitmentModal({
  commitment,
  onClose,
  onCancelled,
}: Readonly<Props>): JSX.Element {
  const [category, setCategory] = useState<AdminCancelCategory>('fraud');
  const [source, setSource] = useState<AdminCancelSource>('manager_manual');
  const [reason, setReason] = useState<string>('');
  const [externalRef, setExternalRef] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const referenceRequired = useMemo(
    () => ADMIN_CANCEL_CATEGORIES_REQUIRING_REFERENCE.has(category),
    [category],
  );

  const reasonOk = reason.trim().length >= 5;
  const refOk = !referenceRequired || externalRef.trim().length > 0;
  const canSubmit = reasonOk && refOk && !submitting;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await commitmentsService.adminCancel(commitment.id, {
        category,
        source,
        reason: reason.trim(),
        external_reference: externalRef.trim() || null,
      });
      const who = fullName({ first_name: commitment.user_first_name, last_name: commitment.user_last_name });
      onCancelled(`Force-cancelled commitment for ${who} on ${commitment.product_name} (${category}).`);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not force-cancel this commitment.'));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedCategoryDescription =
    CATEGORY_OPTIONS.find((c) => c.value === category)?.description ?? '';

  return (
    <Modal open onClose={onClose} busy={submitting} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Force-cancel commitment
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Bypasses the request/approve cycle. The structured category drives the
        Regulatory report — pick the most specific match. Every action
        here is audited as <code>COMMITMENT_FORCE_CANCELLED</code>.
      </p>

      <div
        className="rounded-lg p-3 mb-4 text-sm space-y-1"
        style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}
      >
        <div>
          <span style={{ color: colors.mutedSoft }}>Client:</span>{' '}
          <span style={{ color: colors.ink }}>
            {fullName({ first_name: commitment.user_first_name, last_name: commitment.user_last_name })}
          </span>
        </div>
        <div>
          <span style={{ color: colors.mutedSoft }}>Product:</span>{' '}
          <span style={{ color: colors.ink }}>
            {commitment.product_name} · {commitment.cadence} · {formatMoney(commitment.amount, commitment.currency_code)}
          </span>
        </div>
        <div>
          <span style={{ color: colors.mutedSoft }}>Status:</span>{' '}
          <span style={{ color: colors.ink }}>{commitment.status}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            Cancellation category <span style={{ color: colors.danger }}>*</span>
          </span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as AdminCancelCategory)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink, background: colors.surface }}
          >
            {[...CATEGORY_OPTIONS]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
          </select>
          <span className="block mt-1 text-xs" style={{ color: colors.mutedSoft }}>
            {selectedCategoryDescription}
          </span>
        </label>

        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            External reference{' '}
            {referenceRequired ? (
              <span style={{ color: colors.danger }}>*</span>
            ) : (
              <span className="font-normal" style={{ color: colors.mutedSoft }}>(optional)</span>
            )}
          </span>
          <input
            type="text"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            maxLength={MAX_REF}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
            placeholder={
              referenceRequired
                ? 'Folio CNBV / oficio judicial / compliance ticket id'
                : 'Optional reference (ticket, oficio, etc.)'
            }
          />
        </label>

        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            Source
          </span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as AdminCancelSource)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink, background: colors.surface }}
          >
            {[...SOURCE_OPTIONS]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            Narrative reason <span style={{ color: colors.danger }}>*</span>{' '}
            <span className="font-normal" style={{ color: colors.mutedSoft }}>
              (min 5 chars, max {MAX_REASON})
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={MAX_REASON}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
            placeholder="Explain why this commitment must be cancelled now — auditors read this in years to come."
          />
        </label>

        {error && (
          <div
            className="rounded-lg p-3 text-sm"
            role="alert"
            style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: colors.danger,
              color: '#FFFFFF',
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Force-cancelling…' : 'Force-cancel'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
